import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { db } from './firebase';

/**
 * Publish-time global event index (boat-first IA E1-T6, ra-8en2.1.6).
 *
 * The committee owns the "publish" verb for this artifact — it is built from
 * the same content this site publishes — but the WRITE happens in the
 * `publishEventIndex` cloud function, because the index spans every club and no
 * per-club rules grant can authorise rewriting it. This module is the committee
 * side of that: trigger the rebuild, read back what is live, and (for the page)
 * compare the live doc against the clubs/events actually present so an operator
 * can see when the index has gone stale.
 *
 * See functions/event_index.js and
 * schemas/committee/v1/published/event-index.schema.json.
 */

export const EVENT_INDEX_PATH = 'event_index/current';

/**
 * Trigger a full rebuild of `event_index/current`.
 * Returns `{ success, eventCount, bytes }` from the function.
 */
export async function publishEventIndex() {
  const fn = httpsCallable(functions, 'publishEventIndex');
  const result = await fn({});
  return result.data;
}

/**
 * Read the live index. Returns null when it has never been published — a
 * first-run state the page must distinguish from "published but empty".
 */
export async function loadPublishedEventIndex(firestore = db) {
  const snap = await getDoc(doc(firestore, 'event_index', 'current'));
  if (!snap.exists()) return null;
  return snap.data();
}

/**
 * `${club_id}/${event_id}` — the identity of one index row, and the key the
 * staleness comparison is done on.
 */
export function entryKey(entry) {
  return `${entry?.club_id ?? ''}/${entry?.event_id ?? ''}`;
}

/**
 * Compare the live index against the current clubs/events universe.
 *
 * Pure so it can be tested without Firestore. `liveEvents` is the index's
 * `events` array (or null/undefined when nothing is published); `currentPairs`
 * is `[{ club_id, event_id }]` read straight from the tree.
 *
 * Returns `{ missing, orphaned, inSync }`:
 * - `missing`  — published events the index does not list. These are invisible
 *                to the boat-first home until someone republishes, which is the
 *                failure this page exists to make visible.
 * - `orphaned` — rows for events that no longer exist (deleted since the last
 *                rebuild). Harmless to read but misleading to browse.
 */
export function diffEventIndex(liveEvents, currentPairs) {
  const live = new Set((liveEvents || []).map(entryKey));
  const current = new Set((currentPairs || []).map(entryKey));

  const missing = [...current].filter((k) => !live.has(k)).sort();
  const orphaned = [...live].filter((k) => !current.has(k)).sort();

  return { missing, orphaned, inSync: missing.length === 0 && orphaned.length === 0 };
}

/**
 * List every `{club_id, event_id}` currently in the tree, for the staleness
 * comparison. Walks clubs → events with the client SDK (both are public-read),
 * skipping soft-deleted clubs and events exactly as the builder does — otherwise
 * the page would report a permanent, unfixable drift for every deleted doc.
 */
export async function loadCurrentEventPairs(firestore = db) {
  const clubSnap = await getDocs(collection(firestore, 'clubs'));
  const pairs = [];
  await Promise.all(
    clubSnap.docs
      .filter((c) => (c.data() || {}).deleted !== true)
      .map(async (clubDoc) => {
        const eventSnap = await getDocs(
          collection(firestore, 'clubs', clubDoc.id, 'events'),
        );
        eventSnap.forEach((e) => {
          if ((e.data() || {}).deleted === true) return;
          pairs.push({ club_id: clubDoc.id, event_id: e.id });
        });
      }),
  );
  pairs.sort((a, b) => entryKey(a).localeCompare(entryKey(b)));
  return pairs;
}

/**
 * Best-effort index refresh, for callers whose primary job is something else
 * (publishing a race schedule, say). NEVER throws: a schedule publish that
 * succeeded must not be reported as failed because the derived index could not
 * be rebuilt, and the operator can always rebuild it from the Event Index page.
 * Returns the function's result, or null when the refresh did not happen.
 */
export async function refreshEventIndexQuietly() {
  try {
    return await publishEventIndex();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[eventIndex] background refresh failed', err);
    return null;
  }
}
